<script setup>
const props = defineProps({
  type: String,
  onSearch: {
    type: Function,
    default: null
  }
});

const municipality = useCookie('municipality');
const municipalities = useState('municipalities');
const user = useState('user');

const client = useSupabaseClient();

const { transitionColour } = useTailwindConfig();

// Default search type to inquiry
const searchType = ref('inquiry');

const searchTypes = [
  {
    label: 'Inquiry Assessment',
    value: 'inquiry',
  },
  {
    label: 'Permitted Uses Only',
    value: 'report',
  },
];

const builderMunicipalities = [{
  label: 'Vancouver',
  value: 'vancouver',
},
{
  label: 'Coquitlam',
  value: 'coquitlam',
  isComingSoon: true,
  disabled: true,
}
];

const loading = ref(false);
const dropdownLoading = ref(false);
const searchTimeout = ref(null);

let addressSelected = false;
const inquiryReportState = useState('inquiryReportState');
const initialAddress = computed(() => props.type === 'navbar' ? inquiryReportState.value?.abbreviatedAddress : '');
const address = ref('');
const addresses = ref([]);
const filteredAddresses = ref([]);

const selectedIndex = ref(-1);
const showDropdown = ref(false);

function selectAddress(selectedAddress) {
  address.value = selectedAddress;
  filteredAddresses.value = [];
  selectedIndex.value = -1;

  // Hide dropdown after address selected
  showDropdown.value = false;
  addressSelected = true;
}

// Method to update the address value when a place isn't select from the GmapAutoComplete dropdown, instead just typed. 
// Needed because v-model doesn't work with this component. 
function typeAddress(event) {
  address.value = event.target.value;
  addressSelected = false; // Reset the flag when user types
}

// Method to handle keypress events for the search bar (key up and down through the options)
async function onKeyPress(event) {
  if (event.key === 'Enter') {
    // Prevent form submission initially
    event.preventDefault();

    if (selectedIndex.value >= 0) {
      selectAddress(filteredAddresses.value[selectedIndex.value]);
    }
    else {
      // Submit form
      await submitSearch();
    }

    // Close dropdown
    showDropdown.value = false;
  }
  else if (event.key === 'ArrowDown') {
    // Prevent cursor movement
    event.preventDefault();

    selectedIndex.value = (selectedIndex.value + 1) % filteredAddresses.value.length;

    adjustScroll(true);
  }
  else if (event.key === 'ArrowUp') {
    // Prevent cursor movement
    event.preventDefault();

    selectedIndex.value = (selectedIndex.value - 1 + filteredAddresses.value.length) % filteredAddresses.value.length;

    adjustScroll(false);
  }
}
// Test3
// Function to adjust scrolling in the dropdown when arrow keys are pressed
function adjustScroll(down) {
  nextTick(() => {
    const list = document.getElementById('autocomplete-list');
    const activeItem = list.querySelector('.active');
    if (activeItem) {
      if (down && activeItem.offsetTop + activeItem.offsetHeight > list.offsetHeight + list.scrollTop) {
        list.scrollTop = activeItem.offsetTop + activeItem.offsetHeight - list.offsetHeight;
      } else if (!down && activeItem.offsetTop < list.scrollTop) {
        list.scrollTop = activeItem.offsetTop;
      }
    }
  });
}

// Function for closing dropdown when clicking outside of it
const handleClickOutsideDropdown = (event) => {
  const dropdownElement = document.getElementById('autocomplete-list');
  const inputElement = document.getElementById('search-input');

  // Check if the click occurred outside the dropdown and input
  if (dropdownElement && inputElement && !dropdownElement.contains(event.target) && !inputElement.contains(event.target)) {
    showDropdown.value = false;
  }
};

// Helper function to normalize address strings for autocomplete, so that the ones in the DB and the ones typed by the user are in the exact same format
function normalizeAddress(address) {
  address = address.toLowerCase();

  // If 'waterloo' (for example) is present, ignore everything after it
  const municipalityNameIndex = address.indexOf(municipality.value);
  if (municipalityNameIndex !== -1) {
    address = address.substring(0, municipalityNameIndex);
  }

  // Also if a comma is present, ignore everything after it
  const commaIndex = address.indexOf(',');
  if (commaIndex !== -1) {
    address = address.substring(0, commaIndex);
  }

  // If the last character is a space, delete it
  if (address[address.length - 1] === ' ') {
    address = address.substring(0, address.length - 1);
  }

  // Normalize address abbreviations
  return address.toLowerCase()
    .replace(/\b(crescent|crescen|cresce|cresc)\b/g, 'cres')
    .replace(/\b(avenue|avenu|aven|av)\b/g, 'ave')
    .replace(/\b(street|stree|stre)\b/g, 'st')
    .replace(/\b(road)\b/g, 'rd')
    .replace(/\b(place|plac)\b/g, 'pl')
    .replace(/\b(court|cour)\b/g, 'crt')
    .replace(/\b(drive|driv)\b/g, 'dr')
    .replace(/\b(west|wes)\b/g, 'w')
    .replace(/\b(east|eas)\b/g, 'e')
    .replace(/\b(north|nor)\b/g, 'n')
    .replace(/\b(south|sou)\b/g, 's');
}

// Set the addresses array based on the selected municipality
async function fetchAddressesForDropdown() {
  // No longer needed with server-side filtering
  // We'll keep this function for backward compatibility
  addresses.value = [];
}

// Set the ids array based on the selected municipality
async function fetchIdsForDropdown() {
  const queryParams = {
    select: 'id',
    eqField: 'type',
    eqValue: 'inquiry',
    orderField: 'id',
    orderDirection: 'ASC',
  };
  ids.value = await useCachedData(municipality.value, 'searches', queryParams);
}

// Function to search addresses from the server
async function searchAddresses(searchTerm) {
  if (!searchTerm || searchTerm.length < 2) {
    filteredAddresses.value = [];
    return;
  }

  dropdownLoading.value = true;

  try {
    const { data, error } = await client
      .schema(municipality.value)
      .from('addresses')
      .select('address')
      .ilike('address', `%${searchTerm}%`)
      .order('address', { ascending: true })
      .limit(50);

    if (data) {
      filteredAddresses.value = data.map(a => a.address);
    } else {
      filteredAddresses.value = [];
    }
  } catch (error) {
    console.error('Error searching addresses:', error);
    filteredAddresses.value = [];
  } finally {
    dropdownLoading.value = false;
  }
}

async function submitSearch() {
  loading.value = true;

  let isRollNumber = false;
  if (municipality.value == 'mm') {
    // Roll numbers look like '09280100101680700000'
    // Identify the search as a roll number if it only contains numbers and is at least 10 characters long
    isRollNumber = address.value.match(/^\d{10,}$/);
  }

  // Validate the address by ensuring it exists in the DB
  const { data } = await client
    .schema(municipality.value)
    .from('addresses')
    .select('address')
    .eq('address', address.value)
    .limit(1);

  if (data.length > 0 || isRollNumber) {
    // Call the onSearch callback if provided
    if (props.onSearch) {
      props.onSearch(address.value);
    }

    const formattedAddress = isRollNumber ? address.value : formatAddressForUrl(address.value);
    if (searchType.value == 'inquiry') {
      if (showPlannerView()) {
        // Planner inquiry page
        navigateTo(`/planner-inquiry-report/${formattedAddress}`);
      }
      else {
        // Inquiry page
        navigateTo(`/inquiry-report/${formattedAddress}`);
        useTrackEvent('search');
      }
    }
    else if (searchType.value == 'report') {
      // Report page
      navigateTo(`/report/${formattedAddress}`);
    }
  }
  else {
    // Input not valid. Dont need to show anything here
    loading.value = false;
  }
}

// Watchers:

// Update filteredAddresses when the address changes (default)
watch(address, (newValue) => {
  // Skip search if address was selected from dropdown
  if (addressSelected) {
    addressSelected = false; // Reset for next time
    return;
  }

  // Clear any existing timeout
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  // Set a new timeout to debounce the search
  searchTimeout.value = setTimeout(async () => {
    const normalizedAddress = normalizeAddress(newValue);

    if (normalizedAddress && normalizedAddress.length >= 2) {
      await searchAddresses(normalizedAddress);
    } else {
      filteredAddresses.value = [];
    }

    // Reset selected index when address changes
    selectedIndex.value = -1;
  }, 150); // 150ms debounce
});

// Control visibility of the dropdown
// Control visibility of the dropdown (default)
watch([filteredAddresses, dropdownLoading], ([newFilteredAddresses, isLoading]) => {
  // Show dropdown if we have results or are loading
  if (newFilteredAddresses.length > 0 || isLoading) {
    showDropdown.value = true;
  } else if (!addressSelected) {
    // Hide dropdown if no results and not loading
    showDropdown.value = false;
  } else {
    // Reset bool so changes to address will show dropdown again
    addressSelected = false;
  }
}, { immediate: true });

onMounted(() => {
  address.value = initialAddress.value || '';
  if (props.type != 'navbar') {
    window.addEventListener('keydown', onKeyPress);
  }
  window.addEventListener('click', handleClickOutsideDropdown);

  props.type == fetchAddressesForDropdown();
});

onUnmounted(() => {
  // Remove event listeners when the component unmounts to avoid memory leaks
  if (props.type != 'navbar') {
    window.removeEventListener('keydown', onKeyPress);
  }
  window.removeEventListener('click', handleClickOutsideDropdown);

  // Clear any pending timeouts
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
});
</script>

<template>
  <div class="w-full">
    <!-- Search Bar -->
    <div class="flex flex-col grow sm:flex-row sm:space-x-2" :class="type == 'navbar' ? 'h-9' : 'h-12'">
      <!-- Width of searchbar controlled from parent -->
      <div class="relative w-full"> <!-- Relative positioning needed for the dropdown -->
        <div class="flex border border-gray-300 bg-white text-gray-600"
          :class="[showDropdown ? 'rounded-b-none rounded-t-lg border-b' : 'rounded-lg', type == 'navbar' ? 'text-base' : 'text-lg']">

          <!-- Search Input -->
          <input v-model="address" class="grow pl-4 rounded-l-lg bg-white focus:outline-none hover:bg-gray-50 border-r"
            :class="transitionColour" @input="typeAddress" @keydown="props.type == 'navbar' ? onKeyPress : ''"
            id="search-input" autocomplete="off" data-form-type="other"
            :placeholder="'Search an address' + (municipality == 'mm' ? ' (or roll number)' : '')" />

          <!-- Search Button -->
          <button @click="submitSearch()"
            class="rounded-r-lg flex items-center justify-center text-primary-500 bg-white hover:bg-gray-50 cursor-pointer"
            :class="[type == 'navbar' ? 'w-9 h-9' : 'w-12 h-12', transitionColour]" :disabled="loading">
            <!-- Search Icon -->
            <UIcon name="material-symbols:search-rounded" :class="type == 'navbar' ? 'w-8 h-8' : 'w-10 h-10'" />
          </button>
        </div>

        <!-- Autocomplete Dropdown -->
        <ul v-show="(filteredAddresses.length || dropdownLoading) && showDropdown" id="autocomplete-list"
          class="absolute z-10 bg-white border border-gray-300 border-t-0 text-gray-800 w-full rounded-b-lg max-h-52 overflow-auto transition-all duration-200 ease-out "
          :class="type == 'navbar' ? 'text-base' : 'text-lg'" :style="{ opacity: showDropdown ? 1 : 0 }">
          <!-- Dropdown Items (default) -->
          <li v-for="(item, index) in filteredAddresses.slice(0, 50)"
            class="hover:bg-gray-200 cursor-pointer text-start flex items-center w-full px-2"
            :class="[type == 'navbar' ? 'h-9' : 'h-12', { 'active bg-gray-200': selectedIndex === index }, index == filteredAddresses.length - 1 ? 'rounded-b-lg' : '', transitionColour]"
            @click="selectAddress(item)">
            {{ item }}
          </li>

          <!-- No Results Message -->
          <li v-if="!dropdownLoading && filteredAddresses.length === 0 && address.length >= 2"
            class="p-4 text-gray-500 text-center">
            No addresses found
          </li>
        </ul>
      </div>
    </div>

    <template v-if="type == 'landingPage'">
      <!-- Search Options Section -->
      <div class="flex flex-wrap items-center justify-start gap-4 mt-4 text-sm">
        <!-- Search Type Select -->
        <div v-if="user.subscription != 'builder'" class="flex items-center gap-2">
          <span class="text-custom-text-subtle">Search Type:</span>
          <USelect v-model="searchType" :items="searchTypes" value-key="value" label-key="label" class="w-40"
            size="sm" />
        </div>

        <!-- Municipality Selection -->
        <div v-if="showAdminView() || user.subscription == 'builder'" class="flex items-center gap-2">
          <span class="text-gray-600 font-medium">Municipality:</span>
          <!-- Admin Municipality Select -->
          <USelect v-if="showAdminView()" v-model="municipality" :items="municipalities.map(m => m.name)"
            variant="outline" class="w-32" size="sm" />
          <!-- Builder Municipality Select -->
          <USelect v-else-if="user.subscription == 'builder'" v-model="municipality" value-key="value" label-key="label"
            :items="builderMunicipalities" placeholder="Select municipality" class="w-40" variant="outline" size="sm"
            :disabled-items-with="item => item.isComingSoon">
            <!-- Add a coming soon badge to the dropdown items that are coming soon -->
            <template #item="{ item }">
              <div class="flex items-center justify-between w-full">
                <span class="text-nowrap">{{ item.label }}</span>
                <UBadge v-if="item.isComingSoon" color="gray" size="sm">Coming Soon</UBadge>
              </div>
            </template>
          </USelect>
        </div>
      </div>
    </template>
  </div>
</template>